import React, {useEffect, useState} from "react";
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	TextField,
	Typography,
	Chip,
	Toolbar,
	Button,
	CircularProgress,
	Box,
	IconButton
} from "@material-ui/core";
import {TableSortLabel} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import API from "../../../network/dashboardAPI";
import DietaryPreferenceDialog from "./component/DietaryPreferences";
import ActionButtons from "./component/DeleteDialog";
import useAlert from "../../../services/useAlert";

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
		backgroundColor: "#f9fafc",
		minHeight: "100vh"
	},
	paper: {
		borderRadius: 12,
		overflow: "hidden",
		boxShadow: "0 4px 20px rgba(0,0,0,0.05)"
	},
	toolbar: {
		display: "flex",
		justifyContent: "space-between",
		padding: theme.spacing(2)
	},
	searchBox: {
		display: "flex",
		alignItems: "center",
		padding: "2px 8px",
		borderRadius: 8,
		backgroundColor: "#fff",
		border: "1px solid #ddd",
		width: 300
	},
	searchInput: {
		marginLeft: theme.spacing(1),
		flex: 1
	},
	tableHead: {
		backgroundColor: "#f4f6f8"
	},
	statusChip: {
		fontWeight: "bold"
	},
	actionButton: {
		marginLeft: theme.spacing(1)
	},
	loadingBox: {
		display: "flex",
		justifyContent: "center",
		padding: theme.spacing(3)
	},
	refreshButton: {
		marginLeft: theme.spacing(1)
	},
	actionSection: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1)
	}
}));

export default function DietaryPreferencesList() {
	const classes = useStyles();
	const alert = useAlert();

	const [data, setData] = useState([]);
	const [total, setTotal] = useState(0);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [orderBy, setOrderBy] = useState("createdAt");
	const [order, setOrder] = useState("asc");
	const [loading, setLoading] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(null); // Track which row is being deleted

	// Dialog state
	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState(null); // for edit

	const fetchDietaryPreferences = async () => {
		try {
			setLoading(true);
			const params = {
				search,
				page,
				limit: rowsPerPage,
				sortField: orderBy,
				sortOrder: order,
				_locale: "en"
			};

			const response = await API.getDietaryPreferencesList(params);
			const res = await response.json();

			if (res.success) {
				setData(res.data);
				setTotal(res.total);
				// Show toast if no results found
				if (res.total === 0 && search) {
					alert.toast("No dietary preferences found for your search", "info");
				}
			} else {
				alert.error("Failed to Load", res.message || "Unable to fetch dietary preferences");
			}
		} catch (err) {
			console.error("Error fetching dietary preferences:", err);
			alert.error(
				"Network Error",
				"Failed to load dietary preferences. Please check your connection."
			);
		} finally {
			setLoading(false);
		}
	};

	const handleSort = (field) => {
		if (orderBy === field) {
			setOrder(order === "asc" ? "desc" : "asc");
		} else {
			setOrderBy(field);
			setOrder("asc");
		}
		setPage(0);
	};

	const handleSearch = (e) => {
		setSearch(e.target.value);
		setPage(0);
	};

	const handleSearchSubmit = (e) => {
		if (e.key === "Enter") {
			fetchDietaryPreferences();
		}
	};

	const handleAdd = () => {
		setSelectedItem(null);
		setDialogOpen(true);
	};

	const handleEdit = (item) => {
		setSelectedItem(item);
		setDialogOpen(true);
	};

	const handleDelete = async (row) => {
		try {
			// Show confirmation dialog
			const result = await alert.confirm(
				"Delete Dietary Preference?",
				`Are you sure you want to delete "${row.name}"? This action cannot be undone.`,
				"Yes, Delete",
				"Cancel"
			);

			if (!result.isConfirmed) return;

			setDeleteLoading(row.diet_id);

			const response = await API.deleteDietaryPreference(row.diet_id);
			const res = await response.json();

			if (res.success) {
				alert.success(
					"Deleted Successfully",
					`"${row.name}" has been deleted successfully.`
				);
				alert.toast("Dietary preference deleted");
				fetchDietaryPreferences(); // refresh list after deletion
			} else {
				alert.error(
					"Delete Failed",
					res.message || "Failed to delete dietary preference"
				);
			}
		} catch (err) {
			console.error("Error deleting dietary preference:", err);
			alert.error(
				"Delete Failed",
				"Unable to delete dietary preference. Please try again."
			);
		} finally {
			setDeleteLoading(null);
		}
	};

	const handleRefresh = () => {
		fetchDietaryPreferences();
		alert.toast("Refreshing list...", "info");
	};

	// Refresh list after dialog save
	const handleDialogSaved = (action, itemName) => {
		fetchDietaryPreferences();
		if (action === "add") {
			alert.success(
				"Dietary Preference Added",
				`"${itemName}" has been created successfully.`
			);
			alert.toast("New dietary preference added");
		} else if (action === "edit") {
			alert.success(
				"Dietary Preference Updated",
				`"${itemName}" has been updated successfully.`
			);
			alert.toast("Dietary preference updated");
		}
	};

	// Debounced search effect
	useEffect(() => {
		const timer = setTimeout(() => {
			fetchDietaryPreferences();
		}, 500); // 500ms debounce

		return () => clearTimeout(timer);
	}, [search, page, rowsPerPage, orderBy, order]);

	// Initial load
	useEffect(() => {
		fetchDietaryPreferences();
	}, []);

	return (
		<div className={classes.root}>
			<Typography variant="h5" gutterBottom style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
				Dietary Preferences
				{loading && <CircularProgress size={20}/>}
			</Typography>

			<Paper className={classes.paper}>
				<Toolbar className={classes.toolbar}>
					<div className={classes.searchBox}>
						<SearchIcon color="action"/>
						<TextField
							placeholder="Search dietary preferences..."
							value={search}
							onChange={handleSearch}
							onKeyPress={handleSearchSubmit}
							className={classes.searchInput}
							InputProps={{disableUnderline: true}}
						/>
					</div>
					<div className={classes.actionSection}>
						<IconButton
							onClick={handleRefresh}
							disabled={loading}
							className={classes.refreshButton}
							title="Refresh"
						>
							<RefreshIcon/>
						</IconButton>
						<Button
							variant="contained"
							color="primary"
							onClick={handleAdd}
							disabled={loading}
						>
							Add New
						</Button>
					</div>
				</Toolbar>

				<TableContainer>
					<Table>
						<TableHead className={classes.tableHead}>
							<TableRow>
								<TableCell>
									<TableSortLabel
										active={orderBy === "diet_id"}
										direction={orderBy === "diet_id" ? order : "asc"}
										onClick={() => handleSort("diet_id")}
									>
										ID
									</TableSortLabel>
								</TableCell>
								<TableCell>Image</TableCell>
								<TableCell>
									<TableSortLabel
										active={orderBy === "name"}
										direction={orderBy === "name" ? order : "asc"}
										onClick={() => handleSort("name")}
									>
										Name
									</TableSortLabel>
								</TableCell>
								<TableCell>Description</TableCell>
								<TableCell>
									<TableSortLabel
										active={orderBy === "is_available"}
										direction={orderBy === "is_available" ? order : "asc"}
										onClick={() => handleSort("is_available")}
									>
										Status
									</TableSortLabel>
								</TableCell>
								<TableCell align="right">Actions</TableCell>
							</TableRow>
						</TableHead>

						<TableBody>
							{loading && data.length === 0 ? (
								<TableRow>
									<TableCell colSpan={6}>
										<Box className={classes.loadingBox}>
											<CircularProgress/>
											<Typography variant="body2" style={{marginLeft: 16}}>
												Loading dietary preferences...
											</Typography>
										</Box>
									</TableCell>
								</TableRow>
							) : data.length === 0 ? (
								<TableRow>
									<TableCell colSpan={6} align="center" style={{padding: 40}}>
										<Typography variant="h6" color="textSecondary" gutterBottom>
											No dietary preferences found
										</Typography>
										{search && (
											<Typography variant="body2" color="textSecondary">
												Try adjusting your search terms
											</Typography>
										)}
									</TableCell>
								</TableRow>
							) : (
								data.map((row) => (
									<TableRow hover key={row.diet_id}>
										<TableCell>{row.diet_id}</TableCell>
										<TableCell>
											{row.image_url ? (
												<img
													src={row.image_url}
													alt={row.name}
													style={{
														width: 40,
														height: 40,
														borderRadius: 8,
														objectFit: 'cover'
													}}
													onError={(e) => {
														e.target.style.display = 'none';
														e.target.nextSibling.style.display = 'inline';
													}}
												/>
											) : null}
											<span style={{display: row.image_url ? 'none' : 'inline'}}>
												No Image
											</span>
										</TableCell>
										<TableCell>
											<Typography variant="body2" fontWeight="medium">
												{row.name}
											</Typography>
											{row.name_kh && (
												<Typography variant="caption" color="textSecondary">
													{row.name_kh}
												</Typography>
											)}
										</TableCell>
										<TableCell>
											<Typography variant="body2" style={{maxWidth: 200}}>
												{row.description || "No description"}
											</Typography>
										</TableCell>
										<TableCell>
											<Chip
												label={row.is_available ? "Available" : "Unavailable"}
												color={row.is_available ? "primary" : "secondary"}
												size="small"
												className={classes.statusChip}
											/>
										</TableCell>
										<TableCell align="right">
											<ActionButtons
												row={row}
												handleEdit={handleEdit}
												handleDelete={handleDelete}
												deleteLoading={deleteLoading === row.diet_id}
											/>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>

				<TablePagination
					component="div"
					count={total}
					page={page}
					onPageChange={(e, newPage) => setPage(newPage)}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={(e) => {
						setRowsPerPage(parseInt(e.target.value, 10));
						setPage(0);
					}}
					rowsPerPageOptions={[5, 10, 25, 50]}
				/>
			</Paper>

			{/* Add / Edit Dialog */}
			<DietaryPreferenceDialog
				open={dialogOpen}
				onClose={() => setDialogOpen(false)}
				onSaved={handleDialogSaved}
				initialData={selectedItem}
			/>
		</div>
	);
}